import fetchCycle from '../fetchCycle'

import {
  bookOfferFinalizationService,
  fetchBookingService,
  getCreateBookingService
} from 'services/book'

import {getRiskifiedSessionId} from 'services/riskified'
import captureException from 'utils/captureException'
import {urlParams} from 'utils/urlParams'

import {MiddlewareType} from 'middlewares'

import {
  FETCH_BOOKING,
  FETCH_BOOKING_ERROR,
  FETCH_BOOKING_DONE,
  fetchBookingPending,
  fetchBookingDone,
  fetchBookingError,
  fetchBookingFatalError
} from 'modules/book/actions/fetchBooking'

import {
  BOOK_OFFER_FINALIZATION,
  BOOK_OFFER_FINALIZATION_ERROR,
  BOOK_OFFER_FINALIZATION_DONE,
  bookOfferFinalizationFatalError,
  bookOfferFinalizationPending,
  bookOfferFinalizationDone,
  bookOfferFinalizationError
} from 'modules/book/actions/bookOfferFinalization'

import {psd2AuthError, startPsd2AuthChallenge} from 'modules/psd2Auth/slice'

import {fatalError} from 'modules/common/actions/fatalError'
import {logEvent} from 'modules/analytics/actions/logEvent'

import {determineBookingApiKey} from 'utils/determineApiKeys'
import Psd2 from 'services/psd2'
import {StoreActionsType} from 'modules'
import {getHttpRequestId} from 'modules/common/selectors'
import timing from 'utils/timing'
import logBoFHService, {
  BoFHServiceStatus,
  BoFHServiceEndpointNames
} from 'performance/logBoFHService'
import {
  bookOfferCreation,
  bookOfferCreationConfirm,
  bookOfferCreationDone,
  bookOfferCreationError,
  bookOfferCreationFatalError,
  bookOfferCreationPending
} from 'modules/book/slice'
import {navigateToConfirmationPage} from 'modules/browser/actions'
import {getCurrentBookingRequestId} from 'utils/bookingRequestId'

const decoratePayloadWithRiskifiedToken = payload => {
  const sessionId = getRiskifiedSessionId()
  return {...payload, sessionId}
}

const requestsTiming = timing()

const middleware: MiddlewareType =
  store => next => async (action: StoreActionsType) => {
    const {dispatch, getState} = store
    const state = getState()
    const httpRequestId = getHttpRequestId(state)
    const bookingRequestId = getCurrentBookingRequestId()

    let experimentApiKey

    next(action)

    switch (action.type) {
      case FETCH_BOOKING:
        fetchCycle(
          dispatch,
          fetchBookingService(action.params, experimentApiKey),
          fetchBookingPending,
          fetchBookingDone,
          fetchBookingError,
          fetchBookingFatalError
        )
        dispatch(logEvent('services.BoFHAPI.booking-retrieve'))

        requestsTiming.start('fetchBooking')
        logBoFHService(
          BoFHServiceEndpointNames.BookingRetrieve,
          BoFHServiceStatus.start,
          {request_id: httpRequestId}
        )
        break

      case FETCH_BOOKING_ERROR: {
        dispatch(logEvent('services.BoFHAPI.BookingFetchError'))
        const timings = requestsTiming.stop('fetchBooking')
        logBoFHService(
          BoFHServiceEndpointNames.BookingRetrieve,
          BoFHServiceStatus.error,
          {
            error: action?.data?.response?.data,
            timings,
            request_id: httpRequestId
          },
          'error'
        )
        break
      }

      case fetchBookingFatalError.type: {
        const timings = requestsTiming.stop('fetchBooking')
        logBoFHService(
          BoFHServiceEndpointNames.BookingRetrieve,
          BoFHServiceStatus.fatalError,
          {
            response: action?.payload?.toJSON(),
            timings,
            request_id: httpRequestId
          },
          'error'
        )

        captureException(action?.payload)
        dispatch(fatalError(action?.payload))
        break
      }

      case FETCH_BOOKING_DONE: {
        const timings = requestsTiming.stop('fetchBooking')
        dispatch(logEvent('services.BoFHAPI.BookingFetched'))
        logBoFHService(
          BoFHServiceEndpointNames.BookingRetrieve,
          BoFHServiceStatus.done,
          {timings, request_id: httpRequestId}
        )
        break
      }

      case BOOK_OFFER_FINALIZATION_ERROR: {
        const timings = requestsTiming.stop('bookOfferFinalize')
        dispatch(
          logEvent('services.BoFHAPI.BookingFinalizeError', {
            meta: action?.data
          })
        )
        logBoFHService(
          BoFHServiceEndpointNames.BookingFinalize,
          BoFHServiceStatus.error,
          {
            error: action?.data?.details,
            timings,
            httpRequestId,
            booking_request_id: bookingRequestId
          },
          'error'
        )
        dispatch(psd2AuthError(action.data))

        break
      }

      case bookOfferFinalizationFatalError.type: {
        const timings = requestsTiming.stop('bookOfferFinalize')
        logBoFHService(
          BoFHServiceEndpointNames.BookingFinalize,
          BoFHServiceStatus.fatalError,
          {
            response:
              typeof action?.payload === 'object'
                ? action?.payload
                : action?.payload?.toJSON(),
            timings,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId
          },
          'error'
        )
        captureException(action?.payload)
        dispatch(fatalError(action?.payload))
        break
      }

      case BOOK_OFFER_FINALIZATION:
        dispatch(
          logEvent('services.BoFHAPI.BookOfferFinalization', {
            request_id: httpRequestId
          })
        )
        try {
          const response = await Psd2.getProvider().finalizeBooking()
          const {providerCode} = urlParams()
          /* eslint-disable-next-line fp/no-mutation */
          experimentApiKey = determineBookingApiKey(providerCode)
          const bookOfferFinalizationServiceInstance =
            bookOfferFinalizationService(
              response.link,
              decoratePayloadWithRiskifiedToken({}),
              experimentApiKey,
              httpRequestId,
              bookingRequestId
            )

          requestsTiming.start('bookOfferFinalize')
          logBoFHService(
            BoFHServiceEndpointNames.BookingFinalize,
            BoFHServiceStatus.start,
            {
              response,
              request_id: httpRequestId,
              booking_request_id: bookingRequestId
            }
          )

          fetchCycle(
            dispatch,
            bookOfferFinalizationServiceInstance,
            bookOfferFinalizationPending,
            bookOfferFinalizationDone,
            bookOfferFinalizationError,
            bookOfferFinalizationFatalError
          )
        } catch (error) {
          dispatch(psd2AuthError(error.details))
        }

        break

      case BOOK_OFFER_FINALIZATION_DONE: {
        const timings = requestsTiming.stop('bookOfferFinalize')
        const {data} = action
        logBoFHService(
          BoFHServiceEndpointNames.BookingFinalize,
          BoFHServiceStatus.done,
          {
            response: data,
            timings,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId
          }
        )

        dispatch(bookOfferCreationConfirm(data))
        break
      }

      case bookOfferCreation.type: {
        const {providerCode, serverCode, payload, link} = action.payload

        /* eslint-disable-next-line fp/no-mutation */
        experimentApiKey = determineBookingApiKey(providerCode)

        const Psd2Provider = Psd2.getProvider(serverCode)
        const threeDomainSecureParams = await Psd2Provider.setup()

        if (threeDomainSecureParams) {
          /* eslint-disable-next-line fp/no-mutation */
          payload.threeDomainSecureParams = threeDomainSecureParams
        }

        const bookOfferCreationService = getCreateBookingService(
          link,
          decoratePayloadWithRiskifiedToken(payload),
          experimentApiKey,
          httpRequestId,
          bookingRequestId
        )

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {payment, rooms, ...restPayload} = payload

        const loggedPayload = {
          ...restPayload,
          rooms: rooms.map(room => ({
            guests: room.guests,
            specialRequest: room.specialRequest
          }))
        }

        requestsTiming.start('bookOfferCreation')

        logBoFHService(
          BoFHServiceEndpointNames.BookingCreation,
          BoFHServiceStatus.start,
          {
            params: loggedPayload,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId
          }
        )

        fetchCycle(
          dispatch,
          bookOfferCreationService,
          bookOfferCreationPending,
          bookOfferCreationDone,
          bookOfferCreationError,
          bookOfferCreationFatalError
        )

        break
      }

      case bookOfferCreationDone.type: {
        const timings = requestsTiming.stop('bookOfferCreation')
        const {data} = action.payload
        logBoFHService(
          BoFHServiceEndpointNames.BookingCreation,
          BoFHServiceStatus.done,
          {
            data,
            timings,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId
          }
        )

        if (Psd2.getProvider().isEnrolled(data)) {
          dispatch(startPsd2AuthChallenge())
        } else {
          dispatch(bookOfferCreationConfirm(data))
        }

        break
      }

      case bookOfferCreationError.type: {
        const timings = requestsTiming.stop('bookOfferCreation')
        logBoFHService(
          BoFHServiceEndpointNames.BookingCreation,
          BoFHServiceStatus.error,
          {
            error: action?.payload?.response?.data?.details,
            timings,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId
          },
          'error'
        )

        break
      }

      case bookOfferCreationFatalError.type: {
        const timings = requestsTiming.stop('bookOfferCreation')
        logBoFHService(
          BoFHServiceEndpointNames.BookingCreation,
          BoFHServiceStatus.fatalError,
          {
            response:
              typeof action?.payload === 'object'
                ? action?.payload
                : action?.payload?.toJSON(),
            timings,
            request_id: httpRequestId,
            booking_request_id: bookingRequestId
          },
          'error'
        )
        captureException(action?.payload)
        dispatch(fatalError(action?.payload))
        break
      }

      case bookOfferCreationConfirm.type: {
        dispatch(
          navigateToConfirmationPage({
            bookingId: action?.payload?.id
          })
        )
        break
      }

      default:
        break
    }
  }

export default middleware
